import i18n from '@/i18n';
/**
 * Catchment polygon renderer — draws catchment analysis results on the OpenLayers map.
 *
 * Matches the legacy layer.manager.js conf layer naming, colours and opacity,
 * plus the w.and.d.controller.js drawDtPolygons() / addGeomToLayer() logic.
 */
import { Map as OlMap } from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style, Stroke, Fill } from 'ol/style';
import { getMapColors } from '../themes/mapTheme';

/* ── Conf-layer names (legacy layer.manager.js) ── */
const CONF_LAYER_NAMES = [
  '1stWalkinLayer', '2ndWalkinLayer', '3rdWalkinLayer', '4thWalkinLayer', '5thWalkinLayer',
  '1stDriveLayer',  '2ndDriveLayer',  '3rdDriveLayer',  '4thDriveLayer',  '5thDriveLayer',
  '1stMotorLayer',  '2ndMotorLayer',  '3rdMotorLayer',  '4thMotorLayer',  '5thMotorLayer',
  'dispConfLayer',  'RingBuffer',     'editLayer',
];

/**
 * Build the 18-element colour array from the current theme palette.
 * walk[0-4] → indices 0-4, drive[0-4] → 5-9, motor[0-4] → 10-14,
 * selection → 15 (dispConfLayer), ringBuffer → 16, highlight → 17 (editLayer).
 */
function getConfLayerColors(): string[] {
  const mc = getMapColors();
  return [
    ...mc.walk,               // 0-4
    ...mc.drive,              // 5-9
    ...mc.motor,              // 10-14
    mc.selection,             // 15: dispConfLayer
    mc.ringBuffer,            // 16: RingBuffer
    mc.highlight,             // 17: editLayer
  ];
}

const FILL_OPACITY = 0.18;           // visible fill — enough to distinguish overlapping polygons

/* ── Resolved layer index for a given travelType + time slot ── */
function confLayerIndex(travelTypeRaw: number, timeSlotIdx: number): number {
  if (travelTypeRaw === 1) return timeSlotIdx;                       // walk-in: 0-4
  if ([2, 4, 6, 7].includes(travelTypeRaw)) return timeSlotIdx + 5;    // drive:   5-9
  if (travelTypeRaw === 5) return timeSlotIdx + 10;                  // motor:  10-14
  return 16; // RingBuffer
}

/* ── Cached map of vector layers we add ── */
const catchmentLayers: Record<string, VectorLayer<any>> = {};

function getOrCreateLayer(map: OlMap, name: string, color: string): VectorSource {
  const existing = catchmentLayers[name];
  if (existing) return existing.getSource()!;

  const source = new VectorSource();
  const rgba = hexToRgba(color, FILL_OPACITY);
  const newLayer = new VectorLayer({
    source,
    style: new Style({
      stroke: new Stroke({ color, width: 2 }),
      fill: new Fill({ color: rgba }),
    }),
    zIndex: 300,
    properties: { name, catchmentLayer: true },
  });
  map.addLayer(newLayer);
  catchmentLayers[name] = newLayer;
  return source;
}

function hexToRgba(hex: string, alpha: number): string {
  const bigint = parseInt(hex.replace('#', ''), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

/* ── Public API ── */

export interface CatchmentResponseData {
  StatusRaw: number;
  Status?: string;
  allData?: string[];   // each element is a JSON-stringified object
  analysisType?: number;
}

export interface ParsedPointData {
  name?: string;
  dtGeoms?: DtGeomGroup[];
  travelResults: Record<string, TravelResult> | TravelResult[];
  pointStatistics?: any;
}

export interface DtGeomGroup {
  travelTypeRaw: number;
  timeDtGeoms: TimeDtGeom[];
}

export interface TimeDtGeom {
  geom1?: string;
  geom2?: string;
  geomIntersects?: string;
  // For analysisType=1, the geometry string is at the top level
  [key: string]: any;
}

export interface TravelResult {
  travelType?: string;
  timeResults: Record<string, TimeResult>;
  isActive?: boolean;
}

export interface TimeResult {
  time?: string;
  analysisResults?: AnalysisResult[] | null;
  isActive?: boolean;
}

export interface AnalysisResult {
  layerName: string;
  features: Record<string, any>[];
  keys: string[];
}

const geojsonFormat = new GeoJSON();


/**
 * Parse the raw API response and draw polygons, returning the parsed allData.
 *
 * Closely follows legacy w.and.d.controller.js → drawDtPolygons().
 */
export function drawCatchmentPolygons(
  map: OlMap,
  data: CatchmentResponseData,
): ParsedPointData[] {
  _addGeomCounter = 0;

  const allData = data.allData;
  if (!allData || allData.length === 0) {
    console.warn('[catchment] allData is empty or missing', data);
    return [];
  }

  const analysisType = data.analysisType ?? 1;
  const colors = getConfLayerColors();
  const parsed: ParsedPointData[] = [];

  for (let i = 0; i < allData.length; i++) {
    // Legacy: allData[i] = JSON.parse(allData[i])
    let pointData: any =
      typeof allData[i] === 'string' ? JSON.parse(allData[i] as string) : allData[i];

    // travelResults is always stringified by the backend
    if (typeof pointData.travelResults === 'string') {
      pointData.travelResults = JSON.parse(pointData.travelResults);
    }

    // dtGeoms might be stringified (shouldn't normally be, but guard against it)
    let dtGeoms: any[] | null = pointData.dtGeoms;
    if (typeof dtGeoms === 'string') {
      try { dtGeoms = JSON.parse(dtGeoms); } catch { dtGeoms = null; }
    }

    if (!dtGeoms || !Array.isArray(dtGeoms)) {
      console.warn('[catchment] no dtGeoms in pointData', Object.keys(pointData));
      parsed.push(pointData as ParsedPointData);
      continue;
    }

    for (let j = 0; j < dtGeoms.length; j++) {
      let oneDtGeom = dtGeoms[j];
      if (typeof oneDtGeom === 'string') {
        try { oneDtGeom = JSON.parse(oneDtGeom); } catch { continue; }
      }

      const travelTypeRaw: number = oneDtGeom.travelTypeRaw;
      let timeDtGeoms = oneDtGeom.timeDtGeoms;
      if (typeof timeDtGeoms === 'string') {
        try { timeDtGeoms = JSON.parse(timeDtGeoms); } catch { continue; }
      }
      if (!Array.isArray(timeDtGeoms)) continue;

      for (let k = 0; k < timeDtGeoms.length; k++) {
        try {
          let oneTimeGeom = timeDtGeoms[k];

          // Guard: if oneTimeGeom is a stringified JSON object, parse it
          if (typeof oneTimeGeom === 'string') {
            try { oneTimeGeom = JSON.parse(oneTimeGeom); } catch { /* keep as-is */ }
          }

          const idx = confLayerIndex(travelTypeRaw, k);
          const layerName = CONF_LAYER_NAMES[idx] ?? 'RingBuffer';
          const color = colors[idx] ?? getMapColors().ringBuffer;

          if (analysisType === 1) {
            // Standard catchment: oneTimeGeom IS the GeoJSON geometry (string or object)
            addGeomToMap(map, oneTimeGeom as unknown as string, layerName, color);
          } else {
            // Cannibalization: oneTimeGeom is {geom1, geom2, geomIntersects}
            // Exactly matches legacy: addGeomToLayer(oneTimeGeom.geom1, layerName)
            addGeomToMap(map, oneTimeGeom.geom1, layerName, color);
            addGeomToMap(map, oneTimeGeom.geom2, layerName, color);
            if (oneTimeGeom.geomIntersects != null) {
              addGeomToMap(map, oneTimeGeom.geomIntersects, layerName, color);
            }
          }
        } catch (err) {
          console.error(`[catchment] Error in timeGeom[${j}][${k}]:`, err);
        }
      }
    }

    // Unwrap nested travelResults for the UI (timeResults, analysisResults)
    _unwrapTravelResults(pointData);
    parsed.push(pointData as ParsedPointData);
  }

  // Fit map to combined extent of all catchment features
  fitToCatchmentExtent(map);

  return parsed;
}

/** Deeply unwrap stringified nested fields in travelResults for UI consumption. */
function _unwrapTravelResults(pointData: any) {
  const travelResults = pointData.travelResults;
  if (!travelResults) return;
  const entries = Array.isArray(travelResults) ? travelResults : Object.values(travelResults);
  for (const tr of entries) {
    if (!tr) continue;
    if (typeof tr.timeResults === 'string') {
      try { tr.timeResults = JSON.parse(tr.timeResults); } catch { /* keep */ }
    }
    const timeEntries = Array.isArray(tr.timeResults)
      ? tr.timeResults
      : tr.timeResults ? Object.values(tr.timeResults) : [];
    for (const tmr of timeEntries) {
      if (!tmr) continue;
      if (typeof (tmr as any).analysisResults === 'string') {
        try { (tmr as any).analysisResults = JSON.parse((tmr as any).analysisResults); } catch { /* keep */ }
      }
    }
  }
}

let _addGeomCounter = 0;

/**
 * Parse a GeoJSON geometry and add it to the map as a feature on the named layer.
 * Matches legacy addGeomToLayer(): JSON.parse(geomStr) → readFeature → transform → addFeature.
 */
function addGeomToMap(map: OlMap, geomStr: string | object, layerName: string, color: string) {
  if (geomStr == null) return; // null/undefined guard

  _addGeomCounter++;
  try {
    // Parse string → object (handle possible double-stringification)
    let geomJson: any = geomStr;
    if (typeof geomJson === 'string') {
      geomJson = JSON.parse(geomJson);
      if (typeof geomJson === 'string') geomJson = JSON.parse(geomJson); // double-stringified
    }

    if (!geomJson || !geomJson.type) {
      console.warn('[catchment] addGeomToMap: invalid geometry (no type)', typeof geomStr);
      return;
    }

    const polygon = { type: 'Feature' as const, geometry: geomJson, properties: {} };
    const result = geojsonFormat.readFeature(polygon);
    const feats = Array.isArray(result) ? result : [result];
    const source = getOrCreateLayer(map, layerName, color);

    for (const f of feats) {
      if (!f) continue;
      const geom = f.getGeometry();
      if (geom) geom.transform('EPSG:4326', 'EPSG:3857');
      source.addFeature(f);
    }
  } catch (e) {
    console.error('[catchment] addGeomToMap error:', e,
      typeof geomStr === 'string' ? geomStr.substring(0, 150) : geomStr);
  }
}

function fitToCatchmentExtent(map: OlMap) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let hasFeatures = false;

  Object.values(catchmentLayers).forEach((layer) => {
    const src = layer.getSource();
    if (src && src.getFeatures().length > 0) {
      hasFeatures = true;
      const ext = src.getExtent();
      if (ext[0] < minX) minX = ext[0];
      if (ext[1] < minY) minY = ext[1];
      if (ext[2] > maxX) maxX = ext[2];
      if (ext[3] > maxY) maxY = ext[3];
    }
  });

  if (hasFeatures && isFinite(minX)) {
    map.getView().fit([minX, minY, maxX, maxY], {
      padding: [50, 50, 50, 50],
      duration: 500,
      maxZoom: 16,
    });
  }
}

/**
 * Clear all catchment polygon layers from the map.
 */
export function clearCatchmentPolygons(map: OlMap) {
  Object.values(catchmentLayers).forEach((layer) => {
    map.removeLayer(layer);
  });
  Object.keys(catchmentLayers).forEach((k) => delete catchmentLayers[k]);
}

/**
 * Reset the internal layer cache (call after external code removes catchment layers from the map).
 */
export function resetCatchmentLayerCache() {
  Object.keys(catchmentLayers).forEach((k) => delete catchmentLayers[k]);
}

/**
 * Get the travel type display name.
 */
export function travelTypeName(travelType: number): string {
  switch (travelType) {
    case 1: return i18n.t('travel.walkin');
    case 2: return i18n.t('travel.drive');
    case 3: return i18n.t('travel.ring');
    case 4: return i18n.t('travel.heavy');
    case 5: return i18n.t('travel.motor');
    case 6: return i18n.t('travel.no_traffic');
    case 7: return i18n.t('travel.very_heavy');
    default: return i18n.t('travel.walkin');
  }
}

/**
 * Get the conf-layer colour for a given travel type + time slot index.
 */
export function getCatchmentColor(travelTypeRaw: number, timeSlotIdx: number): string {
  const idx = confLayerIndex(travelTypeRaw, timeSlotIdx);
  const colors = getConfLayerColors();
  return colors[idx] ?? getMapColors().ringBuffer;
}
